import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import NextAction from "../../utils/NextAction";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useState } from "react";

function createData(
  vertex: string,
  dist?: number,
  visited?: boolean,
  previous?: string
) {
  return { vertex, dist, visited: visited, previous };
}

const rows = [
  [
    createData("A", null, false, null),
    createData("B", null, false, null),
    createData("C", null, false, null),
    createData("D", null, false, null),
    createData("E", null, false, null),
    createData("F", null, false, null),
  ],
  [
    createData("A", 0, false, null),
    createData("B", null, false, null),
    createData("C", null, false, null),
    createData("D", null, false, null),
    createData("E", null, false, null),
    createData("F", null, false, null),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, false, "A"),
    createData("C", null, false, null),
    createData("D", null, false, null),
    createData("E", null, false, null),
    createData("F", null, false, null),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, false, "A"),
    createData("C", null, false, null),
    createData("D", null, false, null),
    createData("E", null, false, null),
    createData("F", 4, false, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, false, "B"),
    createData("D", null, false, null),
    createData("E", null, false, null),
    createData("F", 4, false, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, false, "B"),
    createData("D", 6, false, "B"),
    createData("E", null, false, null),
    createData("F", 4, false, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, true, "B"),
    createData("D", 5, false, "C"),
    createData("E", null, false, null),
    createData("F", 4, false, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, true, "B"),
    createData("D", 5, false, "C"),
    createData("E", 7, false, "C"),
    createData("F", 4, false, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, true, "B"),
    createData("D", 5, false, "C"),
    createData("E", 7, false, "C"),
    createData("F", 4, true, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, true, "B"),
    createData("D", 5, true, "C"),
    createData("E", 6, false, "C"),
    createData("F", 4, true, "A"),
  ],
  [
    createData("A", 0, true, null),
    createData("B", 2, true, "A"),
    createData("C", 4, true, "B"),
    createData("D", 5, true, "C"),
    createData("E", 6, true, "C"),
    createData("F", 4, true, "A"),
  ],
];

const texts = [
  `First, we choose the start node (we choose A here) and fill in the distance table below according to the following tutorial. There are three columns: Distance, Visited, Previous.

  - Distance: the current shortest distance from Vertex A.
  - Visited: indicates whether all possible shortest paths have been checked.
  - Previous: the previous node in the current shortest path.

  At first, we do not know the distance between each node, so all distances are set to infinity`,
  "We know the distance between A and A is 0, so we can update the distance value in the table accordingly.",
  "Let's check the A node and mark 'Visited' as True. First, we update the distance between A and B. We know the distance between these two nodes is 2, so we replace infinity with 2 and label 'A' in the 'Previous Node' column.",
  "Following the last step, we update the distance between A and F.",
  "After finishing the process of checking A, we start to trace another node. We find the shortest path first, which is A -> B. We label B as True in the Visited column. Because the distance between B and C is 2, we also update the shortest distance between A and C to 4.",
  "Check the distance from B to D, which is 4. Update the shortest distance from A to D to 6 and label the previous node of D as B.",
  "Next, because the distances between A and C and A and F are both 4, we can choose either one of them. We choose C first in alphabetical order. We label C as True in the Visited column. There are two neighbors of C: D and E. We check D first in alphabetical order. The distance between C and D is 1, so we update the shortest distance to D to 4 + 1 = 5.",
  "Then, we check the other neighbor of C: E. The distance between C and E is 3, and the new shortest distance from A to E is 7. Update the shortest distance from A to E as 7.",
  "There are three unvisited nodes and we choose F for the next step because its distance is the shortest. Label F as True in the Visited column. The new shortest distance from A to F to E is 7, which is the same as the original. Therefore, we don't change the shortest value of E.",
  "Finally, we only have D and E left that are not visited. The shorter node is D, so we check D. Two of the neighbors of D are B and C, which are already visited. We find that the distance between D and E is 1, and the distance between A and E becomes 6, which is shorter than the original value. We update the table and label D as True in the Visited column.",
  "Now, only E is left unvisited. Upon checking the neighbors of E, we find that every new distance is greater than the original value. Therefore, we do not need to update the distance value in this step. We can mark E as True in the Visited column.",
];

interface ContrlProps {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

export default function Controls({ step, setStep }: ContrlProps) {
  return (
    <div className="controls">
      <div>
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Dijkstra's Algorithm
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Here, we introduce the Dijkstra's Algorithm here. With Dijkstra's
          Algorithm, you can find the shortest path between nodes in a graph.
          Particularly, you can{" "}
          <b>
            find the shortest path from a node (called the "start node") to all
            other nodes in the graph, producing a shortest-path tree.
          </b>
        </Typography>
        {step == 0 && (
          <Typography
            style={{
              paddingBottom: 10,
              textAlign: "left",
              whiteSpace: "pre-line",
            }}
          >
            {texts[step]}
          </Typography>
        )}
        {step != 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Step {step}
            </Typography>
            <Typography
              sx={{ mb: 3, textAlign: "left", whiteSpace: "pre-line" }}
            >
              {texts[step]}
            </Typography>
          </>
        )}
        <TableContainer
          component={Paper}
          sx={{ mb: 2, maxWidth: 600, mr: "auto", ml: "auto" }}
        >
          <Table sx={{ maxWidth: 600 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Vertex</TableCell>
                <TableCell align="right">Shortest Distance From A</TableCell>
                <TableCell align="right">Visited</TableCell>
                <TableCell align="right">Previous Node</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows[step].map((row, i) => (
                <TableRow
                  key={row.vertex}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ fontSize: 18 }}>
                    {row.vertex}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={
                      step !== 0 && row.dist != rows[step - 1][i].dist
                        ? { color: "red", fontWeight: "bold", fontSize: 18 }
                        : { fontSize: 18 }
                    }
                  >
                    {row.dist == null ? "infinity" : row.dist}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={
                      step !== 0 && row.visited != rows[step - 1][i].visited
                        ? { color: "red", fontWeight: "bold", fontSize: 18 }
                        : { fontSize: 18 }
                    }
                  >
                    {row.visited ? "True" : "False"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={
                      step !== 0 && row.previous != rows[step - 1][i].previous
                        ? { color: "red", fontWeight: "bold", fontSize: 18 }
                        : { fontSize: 18 }
                    }
                  >
                    {row.previous || "null"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {step == 0 && (
          <Button
            variant={"contained"}
            sx={{ mb: 2 }}
            onClick={() => setStep(1)}
          >
            Start Tutorial
          </Button>
        )}
        {step != 0 && (
          <>
            <Button
              variant={"contained"}
              sx={{ mb: 2, mr: 4 }}
              onClick={() => setStep(step - 1)}
            >
              Previous
            </Button>
            {step !== 10 && (
              <Button
                variant={"contained"}
                sx={{ mb: 2 }}
                onClick={() => setStep(step + 1)}
              >
                Next
              </Button>
            )}
          </>
        )}
        {step == 10 && (
          <>
            <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
              Great job! You now understand Dijkstra's Algorithm.
              <br />
              However, there is no graph on the road in robotics. We have to
              consider an special case which is grids. Let's see how to do
              Dijkstra's Algorithm for Grids in next page.
            </Typography>
            <NextAction
              label={"Next: Dijkstra's Algorithm for Grids"}
              to="/robotics/dijkstras-for-grids"
            />
          </>
        )}
      </div>
    </div>
  );
}
